'use client'
import React, { ChangeEvent, useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import axiosReq from '@/app/interceptor'
import { toast } from 'react-toastify'
import FormBackgroundLayout from '../components/form-background-layout'
import '../onboarding/onboarding.css'
import useDismissAlert from '../Hooks/useDismissAlert'
import { MessageAlert } from '../components/message-alert'

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
  const hasUppercase = /[A-Z]/.test(email)
  const startsWithNumber = /^\d/.test(email)

  return emailRegex.test(email) && !hasUppercase && !startsWithNumber
}

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useDismissAlert({ message: '' }, 5000)
  const router = useRouter()

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!validateEmail(email)) {
      setError({ message: 'Please enter a valid email address!' })
      return
    }

    setIsLoading(true)
    setError({ message: '' })

    try {
      const response = await axiosReq.post('/app/v2/auth/forgot-password', {
        email
      })

      if (response.status === 200) {
        toast.success(
          'An OTP to reset your password has been sent to your email. Please check your inbox.'
        )
        localStorage.setItem('forgotPassword-email', email)
        router.push('/forgot-password/otp')
      } else {
        setError({
          message:
            response.data.message ||
            'Failed to send password reset OTP. Please try again.'
        })
      }
    } catch (error: any) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        setError({ message: error.response.data.message })
      } else {
        setError({
          message:
            'An error occurred while sending the reset password OTP. Please try again.'
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    setError({ message: '' })
  }

  return (
    <FormBackgroundLayout
      title="Forgot Password"
      subtitle={
        <>
          New here?{' '}
          <Link prefetch href="/onboarding" className="text-white">
            Create Account
          </Link>
        </>
      }
      showBackButton
      onBackButtonClick={() => router.back()}
    >
      <form onSubmit={handleSubmit}>
        <p className="text-white" style={{ fontSize: '12px' }}>
          Provide the email linked to your account.
        </p>
        <div className="form-outline mb-3">
          <label className="form-label text-white" htmlFor="email">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            className="form-control mb-3"
            placeholder="Enter your registered email"
            required
            disabled={isLoading}
            value={email}
            onChange={handleEmailChange}
          />
        </div>

        {error.message && <MessageAlert message={error.message} type="error" />}

        <button
          className="btn btn-primary rounded-pill w-100"
          type="submit"
          disabled={isLoading || !email}
        >
          {isLoading ? 'Hang on...' : 'Next'}
        </button>
      </form>

      {isLoading && (
        <div className="mt-4">
          <div className="spinner-border text-light" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
    </FormBackgroundLayout>
  )
}

export default ForgotPassword
